import { jwtDecode } from "jwt-decode";
// import jwtDecode from "jwt-decode"; // fixed import (default export)
import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import "./Dashboard.css";
import ConfirmPopup from "./ConfirmPopup";
import Billing from "./Billing";
import { secureStorage } from "./utils/secureStorage";

// Use env var for API base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000";

// Toast Component
const Toast = ({ message, onClose }) => (
  <div className="toast">
    {message}
    <button onClick={onClose}>×</button>
  </div>
);

// Component to extract org_id from token
const extractOrgIdFromToken = () => {
  const token = secureStorage.getItem("token");
  if (!token) return null;
  try {
    const decoded = jwtDecode(token);
    return decoded.org_id;
  } catch (e) {
    console.error("Failed to decode token", e);
    return null;
  }
};

// Reusable Overlay Modal
const OverlayModal = ({ onClose, children }) => {
  const overlayRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (overlayRef.current && e.target === overlayRef.current) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  return (
    <div className="modal-overlay" ref={overlayRef}>
      {children}
    </div>
  );
};

// User Registration Modal
const Modal = ({ onClose, onSubmit, newUser, setNewUser }) => (
  <div className="modal">
    <h3>Register New User</h3>
    <input
      type="email"
      placeholder="Email"
      value={newUser.email}
      onChange={(e) => setNewUser((prev) => ({ ...prev, email: e.target.value }))}
    />
    <input
      type="password"
      placeholder="Password"
      value={newUser.password}
      onChange={(e) => setNewUser((prev) => ({ ...prev, password: e.target.value }))}
    />
    <select
      value={newUser.role}
      onChange={(e) => setNewUser((prev) => ({ ...prev, role: e.target.value }))}
    >
      <option value="staff">Staff</option>
      <option value="admin">Admin</option>
    </select>
    <div className="modal-actions">
      <button onClick={onSubmit}>Save</button>
      <button onClick={onClose}>Cancel</button>
    </div>
  </div>
);

// Edit User Modal
const PopupEditor = ({ userId, passwordUpdates, setPasswordUpdates, onChangePassword, onDelete, onClose }) => (
  <div className="modal">
    <h3>Edit User</h3>
    <input
      type="password"
      placeholder="New Password"
      value={passwordUpdates[userId] || ""}
      onChange={(e) =>
        setPasswordUpdates((prev) => ({ ...prev, [userId]: e.target.value }))
      }
    />
    <div className="modal-actions">
      <button onClick={() => onChangePassword(userId)}>Save</button>
      <button onClick={() => onDelete(userId)} style={{ color: "red" }}>Delete User</button>
      <button onClick={onClose}>Cancel</button>
    </div>
  </div>
);

// FAISS Modal
const FaissModal = ({
  onClose,
  documents,
  onDelete,
  onRebuild,
  rebuilding,
  checkingProgress,
  indexProgress,
}) => (
  <div className="modal large-modal">
    <h3>Knowledge Base</h3>

    {/* Document list */}
    <ul>
      {documents.map((doc, idx) => (
        <li key={idx} style={{ display: "flex", justifyContent: "space-between" }}>
          {doc}
          <button onClick={() => onDelete(doc)} style={{ color: "red" }}>
            Delete
          </button>
        </li>
      ))}
    </ul>

    {/* Indexing progress bar */}
    {checkingProgress && (
      <div style={{ marginTop: "12px", width: "100%" }}>
        <p>Indexing Progress: {indexProgress}%</p>
        <div
          style={{
            backgroundColor: "#ddd",
            borderRadius: "4px",
            height: "12px",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${indexProgress}%`,
              backgroundColor: "#4caf50",
              height: "100%",
              transition: "width 0.3s ease-in-out",
            }}
          />
        </div>
      </div>
    )}

    {/* Action buttons */}
    <div className="modal-actions" style={{ marginTop: "16px" }}>
      <button onClick={onRebuild} disabled={rebuilding}>
        {rebuilding ? (
          <>
            <span className="spinner" /> Rebuilding Knowledge...
          </>
        ) : (
          "Rebuild Knowledge"
        )}
      </button>
      <button onClick={onClose}>Close</button>
    </div>
  </div>
);

// DB Modal
const DatabaseModal = ({ onClose, databases, onDelete, onSubmit, dbForm, setDbForm, editingId, onEdit }) => {
  const handleInput = (e) => {
    setDbForm({ ...dbForm, [e.target.name]: e.target.value });
  };

  return (
    <div className="modal large-modal">
      <h3>Connected Databases</h3>
      <ul>
        {databases.map((db) => (
          <li key={db.id}>
            <strong>{db.name}</strong> ({db.db_type}) - {db.host}:{db.port}
            <button onClick={() => onEdit(db)}>Edit</button>
            <button onClick={() => onDelete(db.id)} style={{ color: "red" }}>Delete</button>
          </li>
        ))}
      </ul>
      <hr />
      <h4>{editingId ? "Edit" : "Add"} Database</h4>
      <div className="db-form">
        <input name="name" placeholder="Name" value={dbForm.name || ""} onChange={handleInput} />
        <input name="db_type" placeholder="DB Type" value={dbForm.db_type || ""} onChange={handleInput} />
        <input name="host" placeholder="Host" value={dbForm.host || ""} onChange={handleInput} />
        <input name="port" placeholder="Port" value={dbForm.port || ""} onChange={handleInput} />
        <input name="username" placeholder="Username" value={dbForm.username || ""} onChange={handleInput} />
        <input name="password" type="password" placeholder="Password" value={dbForm.password || ""} onChange={handleInput} />
        <input name="database_name" placeholder="Database Name" value={dbForm.database_name || ""} onChange={handleInput} />
        <button onClick={onSubmit}>{editingId ? "Update" : "Add"} Database</button>
      </div>
      <div className="modal-actions">
        <button onClick={onClose}>Close</button>
      </div>
    </div>
  );
};

// Auth request helper using axios with full URL from env var
const makeAuthorizedRequest = async (axiosConfig) => {
  const token = secureStorage.getItem("token");
  const config = {
    ...axiosConfig,
    url: axiosConfig.url.startsWith("http")
      ? axiosConfig.url
      : `${API_BASE_URL}${axiosConfig.url}`,
    headers: {
      ...axiosConfig.headers,
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    return await axios(config);
  } catch (err) {
    if (err.response?.status === 401) {
      const refreshToken = secureStorage.getItem("refreshToken");
      const refreshRes = await axios.post(`${API_BASE_URL}/refresh`, {
        refresh_token: refreshToken,
      });
      secureStorage.setItem("token", refreshRes.data.access_token);
      config.headers.Authorization = `Bearer ${refreshRes.data.access_token}`;
      return await axios(config);
    }
    throw err;
  }
};


// Dashboard Component
const Dashboard = () => {
  const [analytics, setAnalytics] = useState({ daily_counts: [], top_users: [] });
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [users, setUsers] = useState([]);
  const [roleUpdates, setRoleUpdates] = useState({});
  const [passwordUpdates, setPasswordUpdates] = useState({});
  const [activeEditorUserId, setActiveEditorUserId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", password: "", role: "staff" });
  const [toastMsg, setToastMsg] = useState("");
  const [showFaissModal, setShowFaissModal] = useState(false);
  const [faissDocs, setFaissDocs] = useState([]);
  const [rebuilding, setRebuilding] = useState(false);
  const [orgId, setOrgId] = useState(null);
  const [databases, setDatabases] = useState([]);
  const [newDb, setNewDb] = useState({});
  const [showDbModal, setShowDbModal] = useState(false);
  const [dbForm, setDbForm] = useState({});
  const [editingDbId, setEditingDbId] = useState(null);
  

  const [confirmAction, setConfirmAction] = useState(null);

  const [indexProgress, setIndexProgress] = useState(0);
  const [checkingProgress, setCheckingProgress] = useState(false);

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const currentPlan = secureStorage.getItem("tier") || "Free";

  const [orgUsage, setOrgUsage] = useState(null);
  const [userUsages, setUserUsages] = useState([]);

  const [org, setOrg] = useState(null);

  const [sqlUsage, setSqlUsage] = useState({ used: 0, max: 100, percentage: 0 });


  const fetchOrg = async () => {
    try {
      const res = await makeAuthorizedRequest({
        method: "GET",
        url: `/organization/${orgId}`, // Make sure this endpoint exists and is correct
      });
      setOrg(res.data);
    } catch (err) {
      console.error("Failed to fetch organization", err);
    }
  };

  useEffect(() => {
    const fetchSqlUsage = async () => {
      try {
        const res = await makeAuthorizedRequest({
          method: "GET",
          url: "/usage/sql",
        });
        setSqlUsage(res.data);
      } catch (err) {
        console.error("Failed to fetch SQL usage:", err);
      }
    };
  
    fetchSqlUsage();
  }, []);
  


  useEffect(() => {
    console.log("🧠 org (after fix):", org);
    console.log("🧠 org.tier (after fix):", org?.tier);
  }, [org]);
  
  const pollIndexingProgress = (organizationId) => {
    let initialPollDone = false;
  
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/admin/index-progress/${organizationId}`);
        const data = await res.json();
  
        if (data && typeof data.progress === "number") {
          if (!initialPollDone) {
            setCheckingProgress(true);
            initialPollDone = true;
          }
  
          setIndexProgress(data.progress);
  
          if (data.progress >= 100) {
            setTimeout(() => {
              clearInterval(interval);
              setCheckingProgress(false);
              showToast("✅ Indexing complete");
            }, 2000);
          }
        }
      } catch (error) {
        console.error("❌ Failed to fetch progress:", error);
        clearInterval(interval);
        setCheckingProgress(false);
      }
    }, 3000); // Poll every 3 seconds
  };
  
  useEffect(() => {
    const org = extractOrgIdFromToken();
    if (org) setOrgId(org);
  }, []);
  
  useEffect(() => {
    if (orgId) {
      fetchUsers();
      fetchFaissDocs();
      fetchDatabases();
      fetchOrg();
    }
  }, [orgId]);
  
  const fetchUsers = async () => {
    try {
      const res = await makeAuthorizedRequest({ method: "GET", url: "/admin/users" });
      setUsers(res.data);
    } catch (error) {
      console.error("Failed to fetch users", error);
    }
  };
  
  const fetchFaissDocs = useCallback(async () => {
    try {
      const res = await makeAuthorizedRequest({ method: "GET", url: `/admin/faiss_docs/${orgId}` });
      setFaissDocs(res.data.files);
    } catch (error) {
      console.error("Failed to fetch FAISS docs", error);
    }
  }, [orgId]);
  
  const fetchDatabases = useCallback(async () => {
    try {
      const res = await makeAuthorizedRequest({ method: "GET", url: `/admin/databases` });
      setDatabases(res.data);
    } catch (error) {
      console.error("Failed to fetch databases", error);
    }
  }, [orgId]);
  
  const fetchAnalytics = async () => {
    if (!startDate || !endDate) return;
    try {
      const res = await makeAuthorizedRequest({
        method: "GET",
        url: `/admin/analytics?organization_id=${orgId}&start_date=${startDate}&end_date=${endDate}`,
      });
      setAnalytics(res.data);
    } catch (err) {
      console.error("Failed to fetch analytics", err);
    }
  };
  
  useEffect(() => {
    if (orgId && startDate && endDate) fetchAnalytics();
  }, [orgId, startDate, endDate]);
  
  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(""), 3000);
  };
  
  const handleRegister = async () => {
    if (!newUser.email || !newUser.password) return;
  
    try {
      await makeAuthorizedRequest({
        method: "POST",
        url: "/admin/users",
        data: newUser,
      });
  
      setNewUser({ email: "", password: "", role: "staff" });
      fetchUsers();
      showToast("✅ User registered successfully");
      setShowModal(false);
    } catch (error) {
      if (error.response && error.response.status === 403) {
        showToast("❌ User limit reached for this organization. Please upgrade your plan.", "error");
      } else if (error.response && error.response.data?.detail) {
        showToast(`❌ ${error.response.data.detail}`, "error");
      } else {
        showToast("❌ Something went wrong while registering the user.", "error");
      }
    }
  };
  
  const handleRoleChange = async (userId, newRole) => {
    try {
      await makeAuthorizedRequest({
        method: "POST",
        url: `/admin/users/${userId}/role`,
        data: { role: newRole },
      });
      showToast(`✅ Role updated to ${newRole}`);
      fetchUsers();
    } catch (error) {
      if (error.response?.data?.detail) {
        showToast(`❌ ${error.response.data.detail}`, "error");
      } else {
        showToast("❌ Failed to update user role", "error");
      }
    }
  };
  
  const handleChangePassword = async (userId) => {
    const newPassword = passwordUpdates[userId];
    if (!newPassword) return;
  
    try {
      await makeAuthorizedRequest({
        method: "POST",
        url: `/admin/users/${userId}/password`,
        data: { new_password: newPassword },
      });
      setPasswordUpdates((prev) => ({ ...prev, [userId]: "" }));
      showToast("Password updated");
      setActiveEditorUserId(null);
    } catch (error) {
      showToast("❌ Failed to update password", "error");
    }
  };
  
  const handleDeleteUser = (userId) => {
    setConfirmAction({
      message: "Are you sure you want to delete this user?",
      onConfirm: async () => {
        try {
          await makeAuthorizedRequest({ method: "DELETE", url: `/admin/users/${userId}` });
          fetchUsers();
          showToast("User deleted");
          setActiveEditorUserId(null);
        } catch (error) {
          const msg = error.response?.data?.detail || "❌ Failed to delete user";
          showToast(msg);
        } finally {
          setConfirmAction(null);
        }
      },
    });
  };
  
  const handleUpload = async () => {
    if (!file) return;
  
    const allowedTypes = [".pdf", ".doc", ".docx", ".txt"];
    const extension = file.name.split(".").pop().toLowerCase();
    const isValid = allowedTypes.includes(`.${extension}`);
  
    if (!isValid) {
      showToast("❌ Unsupported file type. Please upload a PDF, Word, or TXT file.");
      return;
    }
  
    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);
  
    try {
      const res = await makeAuthorizedRequest({
        method: "POST",
        url: `/admin/upload_pdf/${orgId}`,
        data: formData,
      });
  
      showToast(` ${res.data.message}`);
  
      // Start polling indexing progress after successful upload
      setIndexProgress(0);
      setTimeout(() => pollIndexingProgress(orgId), 1000);
    } catch {
      showToast("❌ Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (checkingProgress) {
      console.log("⏳ Indexing started...");
    } else {
      console.log("✅ Indexing done or not started yet.");
    }
  }, [checkingProgress]);
  
  const handleDeleteDoc = (filename) => {
    setConfirmAction({
      message: `Delete the document "${filename}"?`,
      onConfirm: async () => {
        try {
          await makeAuthorizedRequest({
            method: "DELETE",
            url: `/admin/faiss_docs/${orgId}/${filename}`,
          });
          setFaissDocs((prev) => prev.filter((doc) => doc !== filename));
          showToast("Document deleted");
        } catch (error) {
          const msg = error.response?.data?.detail || "❌ Failed to delete document";
          showToast(msg);
        } finally {
          setConfirmAction(null);
        }
      },
    });
  };
  
  const handleRebuildIndex = async () => {
    setRebuilding(true);
    try {
      // Trigger backend to start rebuild
      await makeAuthorizedRequest({ method: "POST", url: `/admin/rebuild_index/${orgId}` });
      showToast("Rebuild started");
  
      // Reset progress state
      setIndexProgress(0);
      setCheckingProgress(true);
  
      // Call existing polling function
      pollIndexingProgress(orgId);
    } catch (error) {
      showToast("❌ Failed to rebuild knowledge");
      setCheckingProgress(false);
    } finally {
      setRebuilding(false);
    }
  };
  
  const handleAddOrEditDatabase = async () => {
    const dataToSend = { ...dbForm };
    try {
      if (editingDbId) {
        await makeAuthorizedRequest({
          method: "PUT",
          url: `/admin/databases/${editingDbId}`,
          data: dataToSend,
        });
        showToast("Database updated");
      } else {
        await makeAuthorizedRequest({
          method: "POST",
          url: "/admin/databases",
          data: dataToSend,
        });
        showToast("Database added");
      }
      fetchDatabases();
      setDbForm({});
      setEditingDbId(null);
    } catch {
      showToast("❌ Failed to save database");
    }
  };
  
  const handleEditDatabase = (db) => {
    setDbForm({ ...db });
    setEditingDbId(db.id);
    setShowDbModal(true); // Show modal immediately on edit
  };
  
  const handleDeleteDatabase = (id) => {
    setConfirmAction({
      message: "Delete this database?",
      onConfirm: async () => {
        try {
          await makeAuthorizedRequest({ method: "DELETE", url: `/admin/databases/${id}` });
          fetchDatabases();
          showToast("Database removed");
        } catch (error) {
          const msg = error.response?.data?.detail || "❌ Failed to delete database";
          showToast(msg);
        } finally {
          setConfirmAction(null);
        }
      },
    });
  };
  
  const user = JSON.parse(secureStorage.getItem("user")) || {};
  
  useEffect(() => {
    makeAuthorizedRequest({
      method: "GET",
      url: "/admin/usage-summary",
    })
      .then((res) => {
        setOrgUsage(res.data.org);
        setUserUsages(res.data.users);
      })
      .catch((err) => {
        console.error("Failed to fetch usage summary", err);
      });
  }, []);
  
  return (
    <div className="dashboard-container">
      <div className="dashboard-content">
        <div className="dashboard-header">
          <h2 className="dashboard-title">Settings</h2>
          <p className="current-plan-text">
            Current Plan: <strong>{currentPlan}</strong>
          </p>
          <button className="upgrade-button" onClick={() => setShowUpgradeModal(true)}>
            Upgrade Plan
          </button>
        </div>

        {/* 🧠 Org usage bar */}
        {orgUsage && org?.tier?.token_cap_per_user != null && (
          <div className="org-usage-bar">
            {(() => {
              const tokenCapPerUser = Number(org.tier.token_cap_per_user);
              const maxUsers = org.max_users || users.length || 1; // fallback
              // const orgTokenCap = tokenCapPerUser * maxUsers;
              const orgTokenCap = orgUsage.token_cap;

              const tokensUsed = orgUsage.tokens_used || 0;
              const percent = Math.round((tokensUsed / orgTokenCap) * 100);

              console.log("🧠 Token Cap Per User:", tokenCapPerUser);
              console.log("🧠 Max Users:", maxUsers);
              console.log("🧠 Org Cap:", orgTokenCap);
              console.log("🧠 Tokens Used:", tokensUsed);

              return (
                <>
                  <h4>
                    🧠 Usage: {tokensUsed.toLocaleString()} /{" "}
                    {orgTokenCap.toLocaleString()} tokens ({percent}%)
                  </h4>
                  <progress
                    max={orgTokenCap}
                    value={tokensUsed}
                    className="usage-progress"
                  />
                </>
              );
            })()}
          </div>
        )}

        {/* 🗃️ SQL Usage Bar */}
        {org?.tier?.name === "Basic" && sqlUsage && sqlUsage.max != null && (
          <div className="org-usage-bar">
            {(() => {
              const { used, max, percentage } = sqlUsage;

              return (
                <>
                  <h4>
                    🗃️ SQL Usage: {used.toLocaleString()} / {max.toLocaleString()} queries ({percentage}%)
                  </h4>
                  <progress
                    max={max}
                    value={used}
                    className="usage-progress"
                  />
                </>
              );
            })()}
          </div>
        )}


        


  
        {/* Document Upload Section */}
        <section className="dashboard-section">
          <h3>Upload Document</h3>
          <div className="upload-controls">
            <input 
              type="file" 
              className="file-input" /* ✅ Add this class */
              onChange={(e) => setFile(e.target.files[0])} 
            />
            {/* <input type="file" onChange={(e) => setFile(e.target.files[0])} /> */}
            <button onClick={handleUpload} disabled={uploading}>
              {uploading ? <span className="spinner" /> : "Upload"}
            </button>
            <button onClick={() => { fetchFaissDocs(); setShowFaissModal(true); }}>
              Manage Knowledge Base
            </button>
  
            {checkingProgress && !showFaissModal && (
              <div style={{ marginTop: "8px", width: "100%" }}>
                <p>Indexing Progress: {indexProgress}%</p>
                <div style={{
                  backgroundColor: "#ddd",
                  borderRadius: "4px",
                  height: "12px",
                  width: "100%",
                  overflow: "hidden",
                }}>
                  <div style={{
                    width: `${indexProgress}%`,
                    backgroundColor: "#4caf50",
                    height: "100%",
                    transition: "width 0.3s ease-in-out",
                  }} />
                </div>
              </div>
            )}
          </div>
        </section>
  
        {/* Connected Databases Section */}

        <section className="dashboard-section">
        <div className="user-table-header">
          <h3>Connected Databases</h3>
          {currentPlan.toLowerCase() === "free" ? (
            <div className="disabled-tooltip-wrapper">
              <button className="plus-button disabled" disabled>
                ＋
              </button>
              <span className="tooltip-text">Upgrade to use this feature</span>
            </div>
          ) : (
            <button onClick={() => setShowDbModal(true)} className="plus-button">
              ＋
            </button>
          )}
        </div>

        <table className="dashboard-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Host</th>
              <th>Port</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {databases.map((db) => (
              <tr key={db.id}>
                <td>{db.name}</td>
                <td>{db.db_type}</td>
                <td>{db.host}</td>
                <td>{db.port}</td>
                <td>
                  <button onClick={() => handleEditDatabase(db)}>Edit</button>
                  <button
                    onClick={() => handleDeleteDatabase(db.id)}
                    style={{ color: "red" }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>




  
        {/* Users Section */}
        <section className="dashboard-section">
          <div className="user-table-header">
            <h3>Users in Organization</h3>
            <button className="add-user-button" onClick={() => setShowModal(true)}>+</button>
          </div>
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Usage</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const userUsage = (userUsages || []).find((u) => u.user_id === user.id);
                // const userUsage = userUsages.find((u) => u.user_id === user.id);
                const tokensUsed = userUsage?.tokens_used || 0;
                // const percent = orgUsage ?.token_cap ? Math.round((tokensUsed / orgUsage.token_cap) * 100) : 0;
                const tokenCapPerUser = org?.tier?.token_cap_per_user || 5000;
                const percent = Math.round((tokensUsed / tokenCapPerUser) * 100);

                return (
                  <tr key={user.id}>
                    <td>{user.email}</td>
                    <td>
                      <select
                        value={roleUpdates[user.id] || user.role}
                        onChange={(e) =>
                          setRoleUpdates((prev) => ({ ...prev, [user.id]: e.target.value }))
                        }
                      >
                        <option value="staff">Staff</option>
                        <option value="admin">Admin</option>
                      </select>
                      <button onClick={() => handleRoleChange(user.id, roleUpdates[user.id] || user.role)}>Save</button>
                    </td>
                    <td>{tokensUsed.toLocaleString()} ({percent}%)</td>
                    <td>
                      <button onClick={() => setActiveEditorUserId(user.id)}>⋮</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
  
        {/* Modals and Overlays */}
        {showDbModal && (
          <OverlayModal onClose={() => {
            setShowDbModal(false);
            setDbForm({});
            setEditingDbId(null);
          }}>
            <DatabaseModal
              onClose={() => {
                setShowDbModal(false);
                setDbForm({});
                setEditingDbId(null);
              }}
              databases={databases}
              dbForm={dbForm}
              setDbForm={setDbForm}
              onDelete={handleDeleteDatabase}
              onEdit={handleEditDatabase}
              onSubmit={handleAddOrEditDatabase}
              editingId={editingDbId}
            />
          </OverlayModal>
        )}
  
        {activeEditorUserId && (
          <OverlayModal onClose={() => setActiveEditorUserId(null)}>
            <PopupEditor
              userId={activeEditorUserId}
              passwordUpdates={passwordUpdates}
              setPasswordUpdates={setPasswordUpdates}
              onChangePassword={handleChangePassword}
              onDelete={handleDeleteUser}
              onClose={() => setActiveEditorUserId(null)}
            />
          </OverlayModal>
        )}
  
        {showModal && (
          <OverlayModal onClose={() => setShowModal(false)}>
            <Modal
              onClose={() => setShowModal(false)}
              onSubmit={handleRegister}
              newUser={newUser}
              setNewUser={setNewUser}
            />
          </OverlayModal>
        )}
  
        {showFaissModal && (
          <OverlayModal onClose={() => setShowFaissModal(false)}>
            <FaissModal
              onClose={() => setShowFaissModal(false)}
              documents={faissDocs}
              onDelete={handleDeleteDoc}
              onRebuild={handleRebuildIndex}
              rebuilding={rebuilding}
              checkingProgress={checkingProgress}
              indexProgress={indexProgress}
            />
          </OverlayModal>
        )}
  
        {showUpgradeModal && (
          <OverlayModal onClose={() => setShowUpgradeModal(false)}>
            <Billing
              userEmail={user.email}
              userName={user.name}
              userPhone={user.phone}
              orgId={orgId}
            />
          </OverlayModal>
        )}
  
        {toastMsg && <Toast message={toastMsg} onClose={() => setToastMsg("")} />}
  
        {confirmAction && (
          <ConfirmPopup
            message={confirmAction.message}
            onConfirm={confirmAction.onConfirm}
            onCancel={() => setConfirmAction(null)}
          />
        )}
  
        {/* Analytics */}
        <div className="analytics-section">
          <h3>📊 Analytics</h3>
          <div className="date-range-picker">
            <label>
              Start Date:{" "}
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </label>
            <label style={{ marginLeft: "10px" }}>
              End Date:{" "}
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </label>
          </div>
  
          <div style={{ marginTop: "20px" }}>
            <h4>Total Sessions: {analytics?.total_sessions || 0}</h4>
            <h4>Questions Per Day</h4>
            <ul>
              {Array.isArray(analytics?.questions_per_day) && analytics.questions_per_day.length > 0 ? (
                analytics.questions_per_day.map((entry, idx) => (
                  <li key={idx}>
                    {entry.date}: {entry.count} question{entry.count !== 1 ? "s" : ""}
                  </li>
                ))
              ) : (
                <li>No data in selected range</li>
              )}
            </ul>
  
            <h4>Top Users (Leaderboard)</h4>
            <ol>
              {Array.isArray(analytics?.leaderboard) && analytics.leaderboard.length > 0 ? (
                analytics.leaderboard.map((user, idx) => (
                  <li key={idx}>
                    {user.email} — {user.count || user.messages} question
                    {(user.count || user.messages) !== 1 ? "s" : ""}
                  </li>
                ))
              ) : (
                <li>No top users</li>
              )}
            </ol>
          </div>
        </div>
      </div>
    </div>
  );

};
  
export default Dashboard;
  


//   return (
//     <div className="dashboard-container">
//       <div className="dashboard-content">
//         <div className="dashboard-header">
//           <h2 className="dashboard-title">Admin Dashboard</h2>
//           <p className="current-plan-text">
//             Current Plan: <strong>{currentPlan}</strong>
//           </p>
//           <button className="upgrade-button" onClick={() => setShowUpgradeModal(true)}>
//             Upgrade Plan
//           </button>
//         </div>

//       <section className="dashboard-section">
//         <h3>Upload Document</h3>
//         <div className="upload-controls">
//           <input type="file" onChange={(e) => setFile(e.target.files[0])} />
//           <button onClick={handleUpload} disabled={uploading}>
//             {uploading ? <span className="spinner" /> : "Upload"}
//           </button>
//           <button onClick={() => { fetchFaissDocs(); setShowFaissModal(true); }}>
//             Manage Knowledge Base
//           </button>

//           {/* ✅ Indexing progress shown during/after upload */}
//           {checkingProgress && !showFaissModal &&(
//             <div style={{ marginTop: "8px", width: "100%" }}>
//               <p>Indexing Progress: {indexProgress}%</p>
//               <div
//                 style={{
//                   backgroundColor: "#ddd",
//                   borderRadius: "4px",
//                   height: "12px",
//                   width: "100%",
//                   overflow: "hidden",
//                 }}
//               >
//                 <div
//                   style={{
//                     width: `${indexProgress}%`,
//                     backgroundColor: "#4caf50",
//                     height: "100%",
//                     transition: "width 0.3s ease-in-out",
//                   }}
//                 />
//               </div>
//             </div>
//           )}
//         </div>
//       </section>

  
  
//       <section className="dashboard-section">
//         <div className="user-table-header">
//           <h3>Connected Databases</h3>
//           <button onClick={() => setShowDbModal(true)} className="plus-button">＋</button>
//         </div>
//         <table className="dashboard-table">
//           <thead>
//             <tr>
//               <th>Name</th>
//               <th>Type</th>
//               <th>Host</th>
//               <th>Port</th>
//               <th>Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {databases.map((db) => (
//               <tr key={db.id}>
//                 <td>{db.name}</td>
//                 <td>{db.db_type}</td>
//                 <td>{db.host}</td>
//                 <td>{db.port}</td>
//                 <td>
//                   <button onClick={() => handleEditDatabase(db)}>Edit</button>
//                   <button onClick={() => handleDeleteDatabase(db.id)} style={{ color: "red" }}>
//                     Delete
//                   </button>
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </section>
  
//       {showDbModal && (
//         <OverlayModal onClose={() => {
//           setShowDbModal(false);
//           setDbForm({});
//           setEditingDbId(null);
//         }}>
//           <DatabaseModal
//             onClose={() => {
//               setShowDbModal(false);
//               setDbForm({});
//               setEditingDbId(null);
//             }}
//             databases={databases}
//             dbForm={dbForm}
//             setDbForm={setDbForm}
//             onDelete={handleDeleteDatabase}
//             onEdit={handleEditDatabase}
//             onSubmit={handleAddOrEditDatabase}
//             editingId={editingDbId}
//           />
//         </OverlayModal>
//       )}
  
//       <section className="dashboard-section">
//         <div className="user-table-header">
//           <h3>Users in Organization</h3>
//           <button className="add-user-button" onClick={() => setShowModal(true)}>+</button>
//         </div>
//         <table className="dashboard-table">
//           <thead>
//             <tr>
//               <th>Email</th>
//               <th>Role</th>
//               <th>Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {users.map((user) => (
//               <tr key={user.id}>
//                 <td>{user.email}</td>
//                 <td>
//                   <select
//                     value={roleUpdates[user.id] || user.role}
//                     onChange={(e) =>
//                       setRoleUpdates((prev) => ({ ...prev, [user.id]: e.target.value }))
//                     }
//                   >
//                     <option value="staff">Staff</option>
//                     <option value="admin">Admin</option>
//                   </select>
//                   <button onClick={() => handleRoleChange(user.id, roleUpdates[user.id] || user.role)}>Save</button>
//                 </td>
//                 <td>
//                   <button onClick={() => setActiveEditorUserId(user.id)}>⋮</button>
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </section>
  
//       {activeEditorUserId && (
//         <OverlayModal onClose={() => setActiveEditorUserId(null)}>
//           <PopupEditor
//             userId={activeEditorUserId}
//             passwordUpdates={passwordUpdates}
//             setPasswordUpdates={setPasswordUpdates}
//             onChangePassword={handleChangePassword}
//             onDelete={handleDeleteUser}
//             onClose={() => setActiveEditorUserId(null)}
//           />
//         </OverlayModal>
//       )}
  
//       {showModal && (
//         <OverlayModal onClose={() => setShowModal(false)}>
//           <Modal
//             onClose={() => setShowModal(false)}
//             onSubmit={handleRegister}
//             newUser={newUser}
//             setNewUser={setNewUser}
//           />
//         </OverlayModal>
//       )}
  
//       {showFaissModal && (
//         <OverlayModal onClose={() => setShowFaissModal(false)}>
//           <FaissModal
//             onClose={() => setShowFaissModal(false)}
//             documents={faissDocs}
//             onDelete={handleDeleteDoc}
//             onRebuild={handleRebuildIndex}
//             rebuilding={rebuilding}
//             checkingProgress={checkingProgress}
//             indexProgress={indexProgress}
//           />
//         </OverlayModal>
//       )}

//       {showUpgradeModal && (
//         <OverlayModal onClose={() => setShowUpgradeModal(false)}>
//           <Billing
//             userEmail={user.email}
//             userName={user.name}
//             userPhone={user.phone}
//             orgId={orgId}
//           />
//         </OverlayModal>
//       )}

  
//       {toastMsg && <Toast message={toastMsg} onClose={() => setToastMsg("")} />}
  
//       {confirmAction && (
//         <ConfirmPopup
//           message={confirmAction.message}
//           onConfirm={confirmAction.onConfirm}
//           onCancel={() => setConfirmAction(null)}
//         />
//       )}
  
//       <div className="analytics-section">
//         <h3>📊 Analytics</h3>
//         <div className="date-range-picker">
//           <label>
//             Start Date:{" "}
//             <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
//           </label>
//           <label style={{ marginLeft: "10px" }}>
//             End Date:{" "}
//             <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
//           </label>
//         </div>
  
//         <div style={{ marginTop: "20px" }}>
//           <h4>Total Sessions: {analytics?.total_sessions || 0}</h4>
  
//           <h4>Questions Per Day</h4>
//           <ul>
//             {Array.isArray(analytics?.questions_per_day) && analytics.questions_per_day.length > 0 ? (
//               analytics.questions_per_day.map((entry, idx) => (
//                 <li key={idx}>
//                   {entry.date}: {entry.count} question{entry.count !== 1 ? "s" : ""}
//                 </li>
//               ))
//             ) : (
//               <li>No data in selected range</li>
//             )}
//           </ul>
  
//           <h4>Top Users (Leaderboard)</h4>
//           <ol>
//             {Array.isArray(analytics?.leaderboard) && analytics.leaderboard.length > 0 ? (
//               analytics.leaderboard.map((user, idx) => (
//                 <li key={idx}>
//                   {user.email} — {user.count || user.messages} question
//                   {(user.count || user.messages) !== 1 ? "s" : ""}
//                 </li>
//               ))
//             ) : (
//               <li>No top users</li>
//             )}
//           </ol>
//         </div>
//       </div>
//     </div>

//   </div>
//   );

// };

// export default Dashboard;